import { Box, Chip, Stack, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import type { CardsBuckets, UserStub } from '@/types/dashboard'
import type { WidgetProps } from './WidgetProps'
import { EmptyHint } from './EmptyHint'

interface StudentRow {
  student: UserStub
  total: number
  avg_progress: number
  delta_7d: number | null
  delta_30d: number | null
}

interface HistoryPoint {
  date: string
  avg_progress: number
}

const BUCKET_KEYS = ['new', 'learning', 'known', 'mastered'] as const

/** SVG-донат распределения карточек по бакетам (без chart-библиотек) */
function BucketsDonut({ buckets }: { buckets: CardsBuckets }) {
  const theme = useTheme()
  const colors = [
    alpha(theme.palette.text.primary, 0.2),
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
  ]

  const radius = 40
  const stroke = 14
  const circumference = 2 * Math.PI * radius
  const total = Math.max(1, buckets.total)

  let offset = 0
  const segments = BUCKET_KEYS.map((key, index) => {
    const fraction = buckets[key] / total
    const segment = {
      color: colors[index],
      dash: fraction * circumference,
      offset,
    }
    offset += fraction * circumference
    return segment
  })

  return (
    <svg width={100} height={100} viewBox="0 0 100 100" role="img">
      <circle
        cx={50}
        cy={50}
        r={radius}
        fill="none"
        stroke={alpha(theme.palette.text.primary, 0.08)}
        strokeWidth={stroke}
      />
      {segments.map(
        (segment, index) =>
          segment.dash > 0 && (
            <circle
              key={index}
              cx={50}
              cy={50}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={stroke}
              strokeDasharray={`${segment.dash} ${circumference - segment.dash}`}
              strokeDashoffset={-segment.offset}
              transform="rotate(-90 50 50)"
            />
          ),
      )}
      <text
        x={50}
        y={54}
        textAnchor="middle"
        fontSize={18}
        fontWeight={700}
        fill={theme.palette.text.primary}
      >
        {buckets.total}
      </text>
    </svg>
  )
}

/** Спарклайн истории avg_progress (student) */
function HistorySparkline({ history }: { history: HistoryPoint[] }) {
  const theme = useTheme()
  if (history.length < 2) return null

  const width = 160
  const height = 36
  const values = history.map((point) => point.avg_progress)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = Math.max(0.1, max - min)
  const points = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * width
      const y = height - ((value - min) / span) * (height - 4) - 2
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline
        points={points}
        fill="none"
        stroke={theme.palette.primary.main}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}

function DeltaChip({ value, labelKey }: { value: number | null; labelKey: string }) {
  const { t } = useTranslation()
  if (value === null) return null

  return (
    <Chip
      size="small"
      color={value > 0 ? 'success' : value < 0 ? 'warning' : 'default'}
      label={`${value > 0 ? '+' : ''}${value}% ${t(labelKey)}`}
    />
  )
}

/**
 * Прогресс карточек: у учителя — сводка по ученикам + top/bottom,
 * у ученика — собственные бакеты, дельты и история.
 */
export function CardsProgressWidget({ payload, role }: WidgetProps) {
  const { t } = useTranslation()

  const overall = payload.overall as CardsBuckets | undefined
  if (!overall || overall.total === 0) {
    return <EmptyHint fallbackKey="dashboard.widgets.cards_progress.empty" />
  }

  const legend = (
    <Stack spacing={0.25}>
      {BUCKET_KEYS.map((key) => (
        <Typography key={key} variant="caption" color="text.secondary">
          {t(`dashboard.widgets.cards_progress.${key}`)}: <b>{overall[key]}</b>
        </Typography>
      ))}
    </Stack>
  )

  if (role === 'teacher') {
    const overallDeltas = payload.overall as CardsBuckets & {
      delta_7d?: number | null
      delta_30d?: number | null
    }
    const top = (payload.top_students ?? []) as StudentRow[]
    const bottom = (payload.bottom_students ?? []) as StudentRow[]

    return (
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <BucketsDonut buckets={overall} />
          {legend}
          <Stack spacing={0.5}>
            <DeltaChip
              value={overallDeltas.delta_7d ?? null}
              labelKey="dashboard.widgets.cards_progress.delta7"
            />
            <DeltaChip
              value={overallDeltas.delta_30d ?? null}
              labelKey="dashboard.widgets.cards_progress.delta30"
            />
          </Stack>
        </Box>
        {top.length > 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('dashboard.widgets.cards_progress.topStudents')}
            </Typography>
            <Stack spacing={0.25}>
              {top.map((row) => (
                <Box
                  key={row.student.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
                >
                  <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                    {row.student.name}
                  </Typography>
                  <DeltaChip value={row.delta_7d} labelKey="dashboard.widgets.cards_progress.delta7" />
                </Box>
              ))}
            </Stack>
          </Box>
        )}
        {bottom.length > 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('dashboard.widgets.cards_progress.bottomStudents')}
            </Typography>
            <Stack spacing={0.25}>
              {bottom.map((row) => (
                <Box
                  key={row.student.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
                >
                  <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                    {row.student.name}
                  </Typography>
                  <DeltaChip value={row.delta_7d} labelKey="dashboard.widgets.cards_progress.delta7" />
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    )
  }

  const history = (payload.history ?? []) as HistoryPoint[]

  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <BucketsDonut buckets={overall} />
        {legend}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <DeltaChip
          value={(payload.delta_7d as number | null) ?? null}
          labelKey="dashboard.widgets.cards_progress.delta7"
        />
        <DeltaChip
          value={(payload.delta_30d as number | null) ?? null}
          labelKey="dashboard.widgets.cards_progress.delta30"
        />
      </Box>
      <HistorySparkline history={history} />
    </Stack>
  )
}
